import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import debounce from 'lodash.debounce';
import { Button, Select, Text, Tooltip, Input, DatePicker } from 'v3/components';
import { BsQuestionCircleFill } from 'react-icons/bs';
import { createNumberMask } from 'text-mask-addons';
import CurrencyInput from 'v3/components/CurrencyInput';
import { OccurrencesAditionalFieldsContext } from 'contexts/OccurrencesAditionalFields';
import IconButton from 'v3/components/Button/Icon';
import {
  BodyContainer,
  ButtonContainer,
  CenteredItem,
  HeadingContainer,
  StyledButon,
} from './styles';
import { CF_MASK, CF_SLUGS } from 'v3/utils/masks';

const ExtraFieldComponent = React.memo(function ExtraFieldComponent({ index }) {
  const { availableFields, setExtraFields, extraFields } = useContext(
    OccurrencesAditionalFieldsContext,
  );
  const currentField = useMemo(() => extraFields[index], [index, extraFields]);
  const [inputValue, setInputvalue] = useState(currentField.value);

  const debouncedSave = useCallback(
    debounce((nextValue) => {
      setExtraFields((prevState) => {
        const newExtraFields = [...prevState];
        newExtraFields[index] = {
          ...newExtraFields[index],
          value: nextValue || '',
          error: '',
        };
        return newExtraFields;
      });
    }, 100),
    [],
  );

  const handleChangeInput = (e, maxLenght = 255) => {
    const { value: nextValue } = e.target;
    if (nextValue.length <= maxLenght) {
      setInputvalue(nextValue);
      debouncedSave(nextValue);
    }
  };

  const handleChangeOption = (e) => {
    if (e && e != null && e != '') {
      const nextValue = e.value;
      setInputvalue(nextValue);
      debouncedSave(nextValue);
    }
  };

  const handleDeleteField = useCallback(() => {
    setExtraFields((prevState) => {
      const newExtraFields = [...prevState];
      newExtraFields.splice(index, 1);
      return newExtraFields;
    });
  }, [setExtraFields]);

  const handleChangeSelect = (selected) => {
    setExtraFields((prevState) => {
      const newExtraFields = [...prevState];
      newExtraFields[index] = {
        field: {
          ...selected,
          error: '',
        },
        value: '',
      };
      return newExtraFields;
    });
  };

  const renderSpecificInput = () => {
    const fieldType = currentField.field?.fieldType;
    const isMasked = CF_SLUGS.includes(currentField?.field?.value);

    // Option [1,2,...]
    if (fieldType.indexOf('option') > -1) {
      const options = [];
      try {
        if (fieldType.indexOf('(') > -1) {
          const stringValues = fieldType.substring(
            fieldType.indexOf('(') + 1,
            fieldType.length - 1,
          );
          if (stringValues.indexOf(',')) {
            const listValues = stringValues.split(',');
            for (let i = 0; i < listValues.length; i++) {
              options.push({
                label: listValues[i],
                value: listValues[i],
              });
            }
          }
        }
      } catch (ex) { }

      return (
        <Col xs={12}>
          <div style={{ marginTop: 20 }}>
            <Select
              label="Conteúdo"
              onChange={handleChangeOption}
              options={options}
              value={
                inputValue == ''
                  ? ''
                  : {
                    label: inputValue,
                    value: inputValue,
                  }
              }
              error={currentField?.error}
              disableClear
            />
          </div>
        </Col>
      );
    }

    // Date
    if (fieldType === 'datetime') {
      return (
        <Col xs={12}>
          <div style={{ marginTop: 20 }}>
            <DatePicker
              value={inputValue}
              onChange={handleChangeInput}
              label="Conteúdo"
              error={currentField?.error}
              withTime
            />
          </div>
        </Col>
      );
    }

    // Decimal R$ / Number (max,dec)
    if (fieldType.indexOf('decimal') > -1 || fieldType.indexOf('number') > -1) {
      let numberInteger = 10;
      let numberDecimal = 2;
      try {
        if (fieldType.indexOf('(') > -1) {
          const numberValues = fieldType.substring(
            fieldType.indexOf('(') + 1,
            fieldType.length - 1,
          );
          if (numberValues.indexOf(',')) {
            const numberArray = numberValues.split(',');
            numberInteger = parseInt(numberArray[0]);
            numberDecimal = parseInt(numberArray[1]);
          }
        }
      } catch (ex) { }

      const currencyMask = createNumberMask({
        prefix: fieldType.indexOf('decimal') > -1 ? 'R$' : '',
        suffix: '',
        includeThousandsSeparator: true,
        thousandsSeparatorSymbol: '.',
        allowDecimal: numberDecimal > 0,
        decimalSymbol: ',',
        decimalLimit: numberDecimal,
        integerLimit: numberInteger,
        allowNegative: false,
        allowLeadingZeroes: true,
      });

      return (
        <Col xs={12}>
          <div style={{ marginTop: 20 }}>
            <CurrencyInput
              value={inputValue}
              onChange={handleChangeInput}
              label="Conteúdo"
              mask={currencyMask}
              error={currentField?.error}
            />
          </div>
        </Col>
      );
    }

    // String / Int (max)
    let maxLenght = 255;
    if (fieldType.indexOf('string') > -1 || fieldType.indexOf('int') > -1) {
      try {
        if (fieldType.indexOf('(') > -1) {
          maxLenght = parseInt(
            fieldType.substring(fieldType.indexOf('(') + 1, fieldType.length - 1),
          );
        }
      } catch (ex) { }
    }

    return (
      <Col xs={12}>
        <div style={{ marginTop: 20 }}>
          <Input
            type={fieldType.indexOf('int') > -1 ? 'number' : 'text'}
            value={inputValue}
            onKeyDown={(e) => {
              const invalidKeys = ['e', 'E', '+', '-'];
              if (fieldType.indexOf('int') > -1 && invalidKeys.includes(e.key)) {
                e.preventDefault();
              }
            }}
            onChange={(e) => handleChangeInput(e, maxLenght)}
            label="Conteúdo"
            error={currentField?.error}
            masked={isMasked}
            mask={isMasked ? CF_MASK : ''}
            guide={false}
          />
        </div>
      </Col>
    );
  };

  useEffect(() => {
    setInputvalue(currentField.value);
  }, [currentField]);

  return (
    <Row>
      <Col lg={5} md={5} sm={5} xs={12}>
        <CenteredItem>
          <Select
            label="Nome do item"
            onChange={handleChangeSelect}
            options={availableFields}
            value={currentField?.field}
            error={currentField?.field?.error}
            disableClear
          />
        </CenteredItem>
      </Col>
      <Col lg={6} md={5} sm={5} xs={10}>
        <Row>{renderSpecificInput()}</Row>
      </Col>
      <Col lg={1} md={2} sm={2} xs={2}>
        <CenteredItem>
          <IconButton
            style={{ marginTop: 20 }}
            onClick={handleDeleteField}
            variant="error"
            icon="FaTrash"
            color="white"
          />
        </CenteredItem>
      </Col>
    </Row>
  );
});

export function Body() {
  const { fields, extraFields, setExtraFields } = useContext(OccurrencesAditionalFieldsContext);

  const handleAddField = () => {
    setExtraFields((prevState) => [
      ...prevState,
      { field: { label: '', value: '', fieldType: '', id: '' }, value: '' },
    ]);
  };

  return (
    <BodyContainer>
      {extraFields.map((field, index) => (
        <ExtraFieldComponent key={field.id} index={index} />
      ))}
      <ButtonContainer>
        <StyledButon
          disabled={fields.allAditionalFields.length <= extraFields.length}
          $color="#099B94"
          onClick={handleAddField}
        >
          Adicionar
        </StyledButon>
      </ButtonContainer>
    </BodyContainer>
  );
}

export function Heading() {
  return (
    <HeadingContainer>
      <Text type="regular" color="dark" weight="500">
        Campos extras da ocorrência
      </Text>
      <Tooltip
        placement="bottom"
        content={
          <Text>
            Campos adicionais que podem ser incluídos em ocorrências, além dos campos pré definidos.
          </Text>
        }
      >
        <BsQuestionCircleFill />
      </Tooltip>
    </HeadingContainer>
  );
}

export function Footer() {
  const { handleSubmitAditionalFields, isFetching } = useContext(OccurrencesAditionalFieldsContext);

  return (
    <Button loading={isFetching} onClick={handleSubmitAditionalFields}>
      Salvar
    </Button>
  );
}
